import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { extractVideoThumbnail } from 'utils'

interface AnnotationSourceOptions {
  source?: string
  scope: string
  onConsumed?: () => void
  onReady: (url?: string) => void
  onReset: () => void
}

/** Owns generated URLs and prevents a pending annotation from crossing media threads. */
export function useCommentAnnotationSource({ source, scope, onConsumed, onReady, onReset }: AnnotationSourceOptions) {
  const request = useRef(0)
  const ownedUrl = useRef<string | null>(null)
  const callbacks = useRef({ onConsumed, onReady, onReset })

  const releaseSource = useCallback(() => {
    request.current += 1
    if (ownedUrl.current) URL.revokeObjectURL(ownedUrl.current)
    ownedUrl.current = null
  }, [])

  useLayoutEffect(() => {
    callbacks.current = { onConsumed, onReady, onReset }
  })

  useLayoutEffect(() => {
    callbacks.current.onReset()
    return releaseSource
  }, [scope, releaseSource])

  useEffect(() => {
    if (!source) return

    const currentRequest = ++request.current
    callbacks.current.onConsumed?.()

    const open = (url?: string, owned = false) => {
      if (request.current !== currentRequest) {
        if (owned && url) URL.revokeObjectURL(url)
        return
      }
      if (ownedUrl.current) URL.revokeObjectURL(ownedUrl.current)
      ownedUrl.current = owned && url ? url : null
      callbacks.current.onReady(url)
    }

    if (source.startsWith('blob:') || source.startsWith('data:')) {
      open(source)
    } else if (/\.(mp4|mov|avi|webm|mkv|m4v)(\?|$)/i.test(source)) {
      extractVideoThumbnail(source)
        .then((thumbnail) => open(thumbnail || undefined, !!thumbnail))
        .catch(() => open())
    } else {
      fetch(source)
        .then((response) => response.blob())
        .then((blob) => open(URL.createObjectURL(blob), true))
        .catch(() => open(source))
    }

    // Clearing the consumed source must not cancel its pending load. A newer
    // request, media scope change, explicit release or unmount invalidates it.
  }, [source])

  return releaseSource
}
